/**
 * Drawer de histórico de execuções de uma rotina agendada.
 *
 * Mostra todas as execuções da rotina com status, timestamps, exit_code
 * e detalhes de saída/erro em tooltip/expand.
 */

import {
  Drawer,
  Stack,
  Table,
  Text,
  Badge,
  Alert,
  Center,
  Loader,
  Tooltip,
  ScrollArea,
} from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { routinesApi, type RoutineExecution, type ExecutionStatus } from '@/api/routines'

// ─── Badge de status de execução ─────────────────────────────────────────────

const STATUS_COLORS: Record<ExecutionStatus, string> = {
  running: 'blue',
  success: 'green',
  failed: 'red',
  skipped_freeze: 'gray',
}

const STATUS_LABELS: Record<ExecutionStatus, string> = {
  running: 'Em andamento',
  success: 'Sucesso',
  failed: 'Falhou',
  skipped_freeze: 'Pulada (freeze)',
}

function ExecutionStatusBadge({ status }: { status: ExecutionStatus }) {
  return (
    <Badge color={STATUS_COLORS[status]} variant="light" size="sm">
      {STATUS_LABELS[status]}
    </Badge>
  )
}

// ─── Formatação de data ───────────────────────────────────────────────────────

function formatDatetime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

// ─── Célula de saída com tooltip ──────────────────────────────────────────────

function OutputCell({ value }: { value: string | null }) {
  if (!value) return <Text size="xs" c="dimmed">—</Text>
  const preview = value.length > 60 ? value.slice(0, 60) + '…' : value
  return (
    <Tooltip
      label={
        <Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>
          {value}
        </Text>
      }
      multiline
      maw={420}
      withArrow
    >
      <Text size="xs" ff="monospace" style={{ cursor: 'help' }}>
        {preview}
      </Text>
    </Tooltip>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface RoutineExecutionsProps {
  tenantId: number
  routineId: number
  routineName: string
  opened: boolean
  onClose: () => void
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function RoutineExecutions({
  tenantId,
  routineId,
  routineName,
  opened,
  onClose,
}: RoutineExecutionsProps) {
  const { data, isLoading, error } = useQuery<RoutineExecution[]>({
    queryKey: ['routine-executions', tenantId, routineId],
    queryFn: async () => {
      const res = await routinesApi.listExecutions(tenantId, routineId)
      return res.data
    },
    enabled: opened && !!tenantId && !!routineId,
  })

  const executions = data ?? []

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Histórico de Execuções
          </Text>
          <Text size="xs" c="dimmed">
            {routineName}
          </Text>
        </Stack>
      }
      position="right"
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="md">
          Não foi possível carregar o histórico. Tente novamente.
        </Alert>
      )}

      {isLoading && (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      )}

      {!isLoading && executions.length === 0 && !error && (
        <Center py="xl">
          <Text size="sm" c="dimmed">
            Nenhuma execução registrada ainda para esta rotina.
          </Text>
        </Center>
      )}

      {!isLoading && executions.length > 0 && (
        <Table striped withTableBorder withColumnBorders fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Início</Table.Th>
              <Table.Th>Fim</Table.Th>
              <Table.Th>Código</Table.Th>
              <Table.Th>Saída / Erro</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {executions.map((exec) => (
              <Table.Tr key={exec.id}>
                <Table.Td>
                  <ExecutionStatusBadge status={exec.status} />
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{formatDatetime(exec.started_at)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{formatDatetime(exec.finished_at)}</Text>
                </Table.Td>
                <Table.Td>
                  {exec.exit_code != null ? (
                    <Text
                      size="xs"
                      ff="monospace"
                      c={exec.exit_code === 0 ? 'green' : 'red'}
                    >
                      {exec.exit_code}
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {exec.error ? (
                    <OutputCell value={exec.error} />
                  ) : (
                    <OutputCell value={exec.output_truncated} />
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Drawer>
  )
}
